import { useCallback, useEffect, useRef, useState } from 'react';

import { Timestamp } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { UploadResult } from 'firebase/storage';

import { useNavigate } from 'react-router-dom';

import dayjs, { Dayjs } from 'dayjs';

import { useEventMutators } from '../dataMutators/useEventMutators';
import { storage } from '../firebase';
import Competition from '../types/Competition';
import { CoursePair } from '../types/Course';
import Event from '../types/Event';
import EventDay from '../types/EventDay';
import League from '../types/League';
import Org from '../types/Org';
import Season from '../types/Season';
import {
  applyTimeToDate,
  calculateSignupOpens,
  calculateSignupCloses,
  calculateGhinIndexDate,
  calculateShowRosterDate,
  calculateNextEventDate,
  findSeasonForDate,
  getEffectiveDefaults,
} from '../utils/eventDefaults';
import { applyUnitSourceDefault } from '../utils/scoringTypeUtils';
import { CombineDateJSTimeJS, generateFirestoreId, ToDayJS } from '../utils/utils';

import { useAutoSave } from './useAutoSave';

export interface UseEventEditorStateProps {
  event: Event;
  league?: League;
  org?: Org;
  seasons?: Season[];
  isNewEvent?: boolean;
  updateEvent?: (event: Partial<Event>) => Promise<void>;
  handleCancel?: () => void;
  handleSubmit?: (event: Event) => void;
  onEventChange?: (event: Event) => void;
}

export interface UseEventEditorStateReturn {
  // State
  updatedEvent: Event;
  deleteConfirm: boolean;
  isMultiDay: boolean;
  isGolf: boolean;
  defaultTime: Dayjs;
  defaultCourseId: React.MutableRefObject<string>;

  // Handlers
  handleUpdate: (updates: Partial<Event>) => void;
  handleImageUpdated: (snapshot: UploadResult | null) => void;
  handleFileSelected: (file: File | null) => void;
  handleDelete: () => void;
  handleEventDateChange: (dates: Dayjs[] | null) => void;
  handleEventTimesChange: (days: EventDay[] | null, coursePairs: CoursePair[]) => void;
  handleCompetitionChange: (competition: Partial<Competition>) => void;
  handleLeagueChange: (league: League | undefined) => void;
  handleSeasonChange: (season: Season | undefined) => void;
  handleSeasonManualChange: (season: Season | undefined) => void;
  handleSignupTimesManualChange: (updates: { signupOpensAt?: Timestamp; signupClosesAt?: Timestamp }) => void;
  handleGhinIndexDateManualChange: (date: Timestamp | undefined) => void;
  onCancel: () => void;
  onSubmit: () => void;
  setDeleteConfirm: (confirm: boolean) => void;
  setIsMultiDay: (isMultiDay: boolean) => void;
  handleIsGolfChange: (isGolf: boolean) => void;
  setDefaultTime: (time: Dayjs) => void;
}

export default function useEventEditorState(props: UseEventEditorStateProps): UseEventEditorStateReturn {
  const { event: initialEvent, league, org, seasons, isNewEvent = false, updateEvent, handleCancel, handleSubmit, onEventChange } = props;
  const { deleteEvent } = useEventMutators();
  const navigate = useNavigate();

  // Get effective defaults from league/org
  const effectiveDefaults = getEffectiveDefaults(league, org);

  const [updatedEvent, setUpdatedEvent] = useState<Event>(initialEvent);

  // Notify parent when event changes
  useEffect(() => {
    if (onEventChange) {
      onEventChange(updatedEvent);
    }
  }, [updatedEvent, onEventChange]);

  // Track which league's defaults have been applied and whether org was available at the time.
  // If the event already has days, defaults were applied in a prior step — pre-populate the ref
  // so LeagueSelector's mount-time onChange doesn't re-apply them and reset dates/course/time.
  const appliedDefaultsForLeagueRef = useRef<string | undefined>(
    initialEvent.days.length > 0 ? initialEvent.leagueId : undefined
  );
  const orgAvailableWhenAppliedRef = useRef(initialEvent.days.length > 0);

  // Auto-select season when seasons become available (handles async loading)
  // This runs when seasons load after the event date has already been set
  useEffect(() => {
    if (!seasons || seasons.length === 0) return;
    if (updatedEvent.seasonManuallyOverridden) return;

    // Check if current seasonId is valid for the current seasons list
    // (handles race condition when league changes - seasons may update before seasonId is cleared)
    const isCurrentSeasonValid = updatedEvent.seasonId && seasons.some((s) => s.itemId === updatedEvent.seasonId);
    if (isCurrentSeasonValid) return; // Already has a valid season

    let matchingSeason: Season | undefined;

    // For catch-all leagues, always use the catch-all season (skip date matching)
    if (league?.isCatchAll) {
      matchingSeason = seasons.find((s) => s.isCatchAll);
    } else if (updatedEvent.days.length > 0) {
      // For regular leagues, use date-based matching
      const eventDate = ToDayJS(updatedEvent.days[0].datetime as Timestamp);
      matchingSeason = findSeasonForDate(eventDate, seasons);
    }

    if (matchingSeason) {
      setUpdatedEvent((prev) => ({
        ...prev,
        seasonId: matchingSeason!.itemId,
        seasonName: matchingSeason!.name,
      }));
    }
  }, [league?.isCatchAll, seasons, updatedEvent.days, updatedEvent.seasonId, updatedEvent.seasonManuallyOverridden]);

  // Backfill leagueName/orgName when league prop loads for a pre-selected league.
  // Handles the case where leagueId is set from URL params before league data is available.
  useEffect(() => {
    if (!league) return;
    setUpdatedEvent((prev) => {
      if (prev.leagueId !== league.itemId) return prev;
      if (prev.leagueName && prev.orgName) return prev;
      return {
        ...prev,
        leagueName: prev.leagueName || league.name,
        orgName: prev.orgName || league.orgName,
      };
    });
  }, [league]);

  // Re-apply org-level defaults when org loads after initial league selection.
  // This handles the race condition where handleLeagueChange fires before org data is available.
  useEffect(() => {
    if (!isNewEvent || !league || !org) return;
    // Only act if defaults were applied for this league but without org
    if (appliedDefaultsForLeagueRef.current !== league.itemId) return;
    if (orgAvailableWhenAppliedRef.current) return;

    const newDefaults = getEffectiveDefaults(league, org);

    setUpdatedEvent((prev) => {
      const updates: Partial<Event> = {
        maxPlayers: newDefaults.maxPlayers,
        teeSheetGroupSize: newDefaults.teeSheetGroupSize,
        ...(newDefaults.signupPriority !== undefined && { participantOrder: newDefaults.signupPriority }),
      };

      // Recalculate timing defaults with org values (unless user manually changed them)
      if (prev.days.length > 0) {
        const eventDate = ToDayJS(prev.days[0].datetime as Timestamp);
        if (!prev.signupTimesManuallySet) {
          updates.signupOpensAt = calculateSignupOpens(eventDate, newDefaults.signupOpensDaysPrior);
          updates.signupClosesAt = calculateSignupCloses(eventDate, newDefaults.signupClosesDaysPrior);
        }
        if (!prev.ghinIndexDateManuallySet && newDefaults.ghinIndexSetDaysPrior >= 0) {
          updates.ghinIndexDate = calculateGhinIndexDate(eventDate, newDefaults.ghinIndexSetDaysPrior);
        }
        if (!prev.showRosterToAllDateManuallySet) {
          updates.showRosterToAllDate = calculateShowRosterDate(eventDate, newDefaults.showRosterToAllDaysPrior);
        }
        if (!prev.showRosterToTeamDateManuallySet) {
          updates.showRosterToTeamDate = calculateShowRosterDate(eventDate, newDefaults.showRosterToTeamDaysPrior);
        }
      }

      return { ...prev, ...updates };
    });

    orgAvailableWhenAppliedRef.current = true;
  }, [isNewEvent, league, org]);

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isMultiDay, setIsMultiDay] = useState<boolean>(initialEvent.days.length > 1);
  const [isGolf, setIsGolfState] = useState<boolean>(initialEvent.isGolf ?? false);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  // Auto-save hook for existing events
  const { queueChange } = useAutoSave<Event>({
    updateFn: updateEvent,
    itemId: initialEvent.itemId,
    enabled: !isNewEvent && !!updateEvent,
  });

  // Helper to queue changes with required fields for auto-save
  const queueChanges = useCallback(
    (changes: Partial<Event>) => {
      if (isNewEvent || !updateEvent) return;
      // Always include required identifiers
      queueChange('orgId', initialEvent.orgId);
      queueChange('leagueId', initialEvent.leagueId);
      // Queue the actual changes
      Object.entries(changes).forEach(([key, value]) => {
        queueChange(key as keyof Event, value);
      });
    },
    [isNewEvent, updateEvent, queueChange, initialEvent.orgId, initialEvent.leagueId]
  );

  const handleUpdate = useCallback((updates: Partial<Event>) => {
    setUpdatedEvent((prev) => ({
      ...prev,
      ...updates,
    }));
    queueChanges(updates);
  }, [queueChanges]);

  const [defaultTime, setDefaultTime] = useState<Dayjs>(
    initialEvent && initialEvent.days.length > 0 && initialEvent.days[0].datetime
      ? ToDayJS(initialEvent.days[0].datetime as Timestamp, initialEvent.timezone)
      : dayjs('2000-01-01T09:00:00')
  );
  const defaultCourseId = useRef(
    initialEvent && initialEvent.days.length > 0 && initialEvent.days[0].courseId ? initialEvent.days[0].courseId : ''
  );

  const onCancel = useCallback(() => {
    if (handleCancel) {
      handleCancel();
    } else {
      navigate(-1);
    }
  }, [handleCancel, navigate]);

  const onSubmit = useCallback(async () => {
    if (handleSubmit) {
      let eventToSave = updatedEvent;

      // If there's a pending logo file (new event with deferred upload), upload it now
      if (pendingLogoFile && isNewEvent) {
        const logoRef = ref(
          storage, 
          `org/${eventToSave.orgId}/league/${eventToSave.leagueId}/event/${eventToSave.itemId}/orgMemberLeagueAdminFile/${eventToSave.itemId}-logo`);
        const snapshot = await uploadBytes(logoRef, pendingLogoFile);
        eventToSave = { ...eventToSave, logo: snapshot.ref.name };
      }

      handleSubmit(eventToSave);
    } else {
      console.log('No submit handler provided.');
    }
  }, [handleSubmit, updatedEvent, pendingLogoFile, isNewEvent]);

  const handleImageUpdated = useCallback((snapshot: UploadResult | null) => {
    if (updatedEvent) {
      const logo = snapshot ? snapshot.ref.name : '';
      setUpdatedEvent({ ...updatedEvent, logo });
      queueChanges({ logo });
    }
  }, [updatedEvent, queueChanges]);

  const handleFileSelected = useCallback((file: File | null) => {
    setPendingLogoFile(file);
  }, []);

  const handleDelete = useCallback(() => {
    if (!initialEvent) return;

    deleteEvent(initialEvent)
      .then(() => {
        navigate('/events');
      })
      .catch((error) => {
        console.error('Error deleting event:', error);
      });
  }, [initialEvent, deleteEvent, navigate]);

  const handleEventDateChange = useCallback((dates: Dayjs[] | null) => {
    if (updatedEvent) {
      setIsGolfState(updatedEvent.isGolf ?? false);
      let newDays: EventDay[];
      if (!dates || dates.length === 0) {
        newDays = [];
      } else if (updatedEvent.days.length === 0) {
        newDays = dates.map((date) => ({
          itemId: generateFirestoreId(),
          datetime: CombineDateJSTimeJS(date, defaultTime),
          courseId: defaultCourseId.current,
          duration: 300,
          isGolf: updatedEvent.isGolf,
        }));
      } else {
        // For single-day events, preserve the existing day's properties when the date changes
        const isSingleDayChange = !isMultiDay && dates.length === 1 && updatedEvent.days.length === 1;

        // flatMap, not map: a calendar date is never a unique identity for an EventDay (this app
        // allows multiple rounds on the same date, e.g. a front-9/back-9 split - date must never
        // be used to correlate/identify one). Confirmed live bug: .find() below returned only the
        // FIRST existing day matching a given date, so re-confirming dates in the calendar picker
        // (EventDateCalendar's onChange, wired to this function from both EventEditor.tsx and
        // EventReviewAndFinalize.tsx) silently dropped every OTHER round already on that date.
        // .filter() + flatMap preserves every existing round on a still-selected date instead.
        newDays = dates.flatMap((date) => {
          const existingDaysForDate = updatedEvent.days.filter((day) => ToDayJS(day.datetime as Timestamp).isSame(date, 'D'));
          if (existingDaysForDate.length > 0) {
            return existingDaysForDate;
          } else if (isSingleDayChange) {
            // For single-day events, preserve the existing day's courseId and other properties
            const existingDay = updatedEvent.days[0];
            return [{
              ...existingDay,
              datetime: CombineDateJSTimeJS(date, ToDayJS(existingDay.datetime as Timestamp)),
            }];
          } else {
            // For multi-day events, use the first existing courseId if available
            const existingCourseId = updatedEvent.days.find((day) => day.courseId)?.courseId;
            return [{
              itemId: generateFirestoreId(),
              datetime: CombineDateJSTimeJS(date, defaultTime),
              courseId: existingCourseId || defaultCourseId.current,
              duration: 300,
              isGolf: updatedEvent.isGolf,
            }];
          }
        });
      }

      // Build the updates object
      const updates: Partial<Event> = { days: newDays };

      // Auto-update signup times if not manually set
      if (dates && dates.length > 0 && !updatedEvent.signupTimesManuallySet) {
        // Re-parse in event timezone so startOf/endOf('day') anchors to the right midnight
        const tz = updatedEvent.timezone || undefined;
        const eventDate = tz ? dayjs.tz(dates[0].format('YYYY-MM-DD'), tz) : dates[0];
        updates.signupOpensAt = calculateSignupOpens(eventDate, effectiveDefaults.signupOpensDaysPrior);
        updates.signupClosesAt = calculateSignupCloses(eventDate, effectiveDefaults.signupClosesDaysPrior);
      }

      // Auto-update GHIN index date if not manually set
      if (dates && dates.length > 0 && !updatedEvent.ghinIndexDateManuallySet && updatedEvent.ghinIndexDate) {
        const tz = updatedEvent.timezone || undefined;
        const eventDate = tz ? dayjs.tz(dates[0].format('YYYY-MM-DD'), tz) : dates[0];
        updates.ghinIndexDate = calculateGhinIndexDate(eventDate, effectiveDefaults.ghinIndexSetDaysPrior);
      }

      // Auto-update roster visibility dates if not manually set
      if (dates && dates.length > 0) {
        const tz = updatedEvent.timezone || undefined;
        const eventDate = tz ? dayjs.tz(dates[0].format('YYYY-MM-DD'), tz) : dates[0];
        if (!updatedEvent.showRosterToAllDateManuallySet) {
          updates.showRosterToAllDate = calculateShowRosterDate(eventDate, effectiveDefaults.showRosterToAllDaysPrior);
        }
        if (!updatedEvent.showRosterToTeamDateManuallySet) {
          updates.showRosterToTeamDate = calculateShowRosterDate(eventDate, effectiveDefaults.showRosterToTeamDaysPrior);
        }
      }

      // Auto-select season if not manually overridden
      if (dates && dates.length > 0 && !updatedEvent.seasonManuallyOverridden && seasons && seasons.length > 0) {
        const eventDate = dates[0];
        const matchingSeason = findSeasonForDate(eventDate, seasons);
        if (matchingSeason && matchingSeason.itemId !== updatedEvent.seasonId) {
          updates.seasonId = matchingSeason.itemId;
          updates.seasonName = matchingSeason.name;
        }
      }

      setUpdatedEvent({ ...updatedEvent, ...updates });
      queueChanges(updates);
    }
  }, [updatedEvent, defaultTime, defaultCourseId, queueChanges, isMultiDay, effectiveDefaults, seasons]);

  const handleEventTimesChange = useCallback((days: EventDay[] | null, coursePairs: CoursePair[]) => {
    if (updatedEvent) {
      if (days) {
        const courseIds = coursePairs.map((cp) => cp.id);
        setUpdatedEvent({ ...updatedEvent, days: days, coursePairs: coursePairs, courseIds });
        queueChanges({ days });
        if (days.length > 0) {
          setDefaultTime(
            ToDayJS(
              days[0].datetime as Timestamp,
              coursePairs.length > 0 ? coursePairs[0].timezone : initialEvent.timezone
            )
          );
        }
      }
    }
  }, [updatedEvent, initialEvent.timezone, queueChanges]);

  const handleCompetitionChange = useCallback((competition: Partial<Competition>) => {
    if (!competition.itemId) return;
    // An edit that changes a game's size stores its unit source default (applyUnitSourceDefault).
    const competitions = (updatedEvent.competitions ?? []).map((c) =>
      c.itemId === competition.itemId ? applyUnitSourceDefault({ ...c, ...competition }, updatedEvent, c) : c
    );
    handleUpdate({ competitions });
  }, [updatedEvent, handleUpdate]);

  const handleLeagueChange = useCallback(
    (newLeague: League | undefined) => {
      const leagueChanged = newLeague?.itemId !== updatedEvent.leagueId;
      const defaultsNeeded = isNewEvent && newLeague && appliedDefaultsForLeagueRef.current !== newLeague.itemId;

      if (!leagueChanged && !defaultsNeeded) return;

      const updates: Partial<Event> = {};

      // Update league identifiers only if league actually changed
      if (leagueChanged) {
        updates.leagueId = newLeague?.itemId;
        updates.leagueName = newLeague?.name;
        updates.orgId = newLeague?.orgId;
        updates.orgName = newLeague?.orgName;
      }

      // For new events, apply defaults from the league/org
      if (defaultsNeeded && newLeague) {
        const newDefaults = getEffectiveDefaults(newLeague, org);
        const playDays = newLeague.defaultPlayDays ?? org?.defaultPlayDays;
        const latestDate = newLeague.latestEventDate instanceof Timestamp ? newLeague.latestEventDate : undefined;
        const nextEventDate = calculateNextEventDate(latestDate, playDays);
        const eventDateWithTime = applyTimeToDate(nextEventDate, newDefaults.startTime);

        // Build initial event day
        const newDays: EventDay[] = [{
          datetime: Timestamp.fromDate(eventDateWithTime.toDate()),
          courseId: newDefaults.coursePair?.id ?? '',
          duration: 300,
          isGolf: true,
        }];

        // Build coursePairs for local rendering only — server manages the Firestore field
        const newCoursePairs: CoursePair[] = newDefaults.coursePair
          ? [newDefaults.coursePair]
          : [];

        // Calculate signup times, GHIN index date, and roster visibility dates
        const signupOpensAt = calculateSignupOpens(nextEventDate, newDefaults.signupOpensDaysPrior);
        const signupClosesAt = calculateSignupCloses(nextEventDate, newDefaults.signupClosesDaysPrior);
        const ghinIndexDate = newDefaults.ghinIndexSetDaysPrior >= 0
          ? calculateGhinIndexDate(nextEventDate, newDefaults.ghinIndexSetDaysPrior)
          : undefined;
        const showRosterToAllDate = calculateShowRosterDate(nextEventDate, newDefaults.showRosterToAllDaysPrior);
        const showRosterToTeamDate = calculateShowRosterDate(nextEventDate, newDefaults.showRosterToTeamDaysPrior);

        // Clear seasonId - the useEffect will auto-select once new seasons load
        // (We can't use findSeasonForDate here because `seasons` prop still has OLD league's seasons)

        Object.assign(updates, {
          // Ensure denormalized name fields are set — for pre-selected leagues
          // (where leagueChanged is false) these are the only place they get populated
          // before the league prop backfill effect has a chance to fire.
          leagueName: newLeague.name,
          orgName: newLeague.orgName,
          days: newDays,
          maxPlayers: newDefaults.maxPlayers,
          teeSheetGroupSize: newDefaults.teeSheetGroupSize,
          // Only set participantOrder if a default is defined (Firebase rejects undefined)
          ...(newDefaults.signupPriority !== undefined && { participantOrder: newDefaults.signupPriority }),
          signupOpensAt,
          signupClosesAt,
          ghinIndexDate,
          showRosterToAllDate,
          showRosterToTeamDate,
          color: newLeague.color,
          seasonId: '',
          seasonName: '',
          // Reset manual override flags since we're applying fresh defaults
          signupTimesManuallySet: false,
          ghinIndexDateManuallySet: false,
          seasonManuallyOverridden: false,
          showRosterToAllDateManuallySet: false,
          showRosterToTeamDateManuallySet: false,
        });

        // Update refs
        setDefaultTime(eventDateWithTime);
        defaultCourseId.current = newDefaults.coursePair?.id ?? '';

        // Mark defaults as applied for this league
        appliedDefaultsForLeagueRef.current = newLeague.itemId;
        orgAvailableWhenAppliedRef.current = !!org;

        // Keep local state in sync for rendering — coursePairs/courseIds are not written to Firestore
        if (newCoursePairs.length > 0) {
          setUpdatedEvent((prev) => ({
            ...prev,
            coursePairs: newCoursePairs,
            courseIds: newCoursePairs.map((cp: CoursePair) => cp.id),
          }));
        }
      }

      handleUpdate(updates);
    },
    [updatedEvent.leagueId, handleUpdate, isNewEvent, org]
  );

  const handleSeasonChange = useCallback(
    (season: Season | undefined) => {
      if (season?.itemId !== updatedEvent.seasonId) {
        handleUpdate({
          seasonId: season?.itemId,
          seasonName: season?.name,
        });
      }
    },
    [updatedEvent.seasonId, handleUpdate]
  );

  // Manual change handlers that set override flags
  const handleSeasonManualChange = useCallback(
    (season: Season | undefined) => {
      handleUpdate({
        seasonId: season?.itemId,
        seasonName: season?.name,
        seasonManuallyOverridden: true,
      });
    },
    [handleUpdate]
  );

  const handleSignupTimesManualChange = useCallback(
    (updates: { signupOpensAt?: Timestamp; signupClosesAt?: Timestamp }) => {
      handleUpdate({
        ...updates,
        signupTimesManuallySet: true,
      });
    },
    [handleUpdate]
  );

  const handleGhinIndexDateManualChange = useCallback(
    (date: Timestamp | undefined) => {
      handleUpdate({
        ghinIndexDate: date,
        ghinIndexDateManuallySet: true,
      });
    },
    [handleUpdate]
  );

  const handleIsGolfChange = useCallback((newIsGolf: boolean) => {
    setIsGolfState(newIsGolf);
    // Update all days to match the event type
    if (updatedEvent && updatedEvent.days.length > 0) {
      const updatedDays = updatedEvent.days.map(day => ({
        ...day,
        isGolf: newIsGolf
      }));
      setUpdatedEvent(prev => ({
        ...prev,
        isGolf: newIsGolf,
        days: updatedDays
      }));
      queueChanges({ isGolf: newIsGolf, days: updatedDays });
    } else {
      setUpdatedEvent(prev => ({ ...prev, isGolf: newIsGolf }));
      queueChanges({ isGolf: newIsGolf });
    }
  }, [updatedEvent, queueChanges]);

  return {
    // State
    updatedEvent,
    deleteConfirm,
    isMultiDay,
    isGolf,
    defaultTime,
    defaultCourseId,

    // Handlers
    handleUpdate,
    handleImageUpdated,
    handleFileSelected,
    handleDelete,
    handleEventDateChange,
    handleEventTimesChange,
    handleCompetitionChange,
    handleLeagueChange,
    handleSeasonChange,
    handleSeasonManualChange,
    handleSignupTimesManualChange,
    handleGhinIndexDateManualChange,
    onCancel,
    onSubmit,
    setDeleteConfirm,
    setIsMultiDay,
    handleIsGolfChange,
    setDefaultTime,
  };
}